import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";

export interface TradeCategory {
  id: string;
  name: string;
  description: string | null;
  csiDivision: string | null;
  sortOrder: number | null;
  isActive: boolean | null;
}

export function useTradeCategories() {
  return useQuery({
    queryKey: ["trade-categories"],
    queryFn: () => apiFetch<TradeCategory[]>("/trade-categories"),
  });
}

export const configKeys = {
  all: (type: string) => ["config", type] as const,
};

export function useConfigItems(type: string) {
  return useQuery({
    queryKey: configKeys.all(type),
    queryFn: () => apiFetch<Record<string, unknown>[]>(`/config/${type}`),
    enabled: type.length > 0,
  });
}

export function useCreateConfigItem(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, unknown>) => apiPost(`/config/${type}`, values),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.all(type) }),
  });
}

export function useUpdateConfigItem(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...values }: Record<string, unknown> & { id: string }) =>
      apiPatch(`/config/${type}/${id}`, values),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.all(type) }),
  });
}

export function useDeleteConfigItem(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/config/${type}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: configKeys.all(type) }),
  });
}
